/**
 * Sprint P23 · Feb 2026 · Advanced Drillhole Analytics Workspace (P1.1–P1.6).
 *
 * Six tabs (Statistics + Histograms + Log-Prob + Box + Contact + Metal-at-Risk)
 * driven by real backend endpoints under /api/projects/{pid}/analytics/*.
 * All chart renderings are pure inline SVG (no chart-library dependency) so
 * exports (PNG/SVG/PDF) can be produced from the DOM tree directly.
 */
import { useState, useMemo } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  ChartBar, ChartLine, ChartScatter, Path, Warning,
  DownloadSimple, Sparkle,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { WizardCard, StatusRow, KV } from "@/pages/wizard/_shared";
import {
  getAnalyticsDataSelection, analyticsHistogram, analyticsLogProbability,
  analyticsBoxPlot, analyticsContactAnalysis, analyticsMetalAtRisk,
  analyticsExport,
} from "@/lib/api";


const TABS = [
  { key: "histogram",   label: "Histograms",     icon: ChartBar },
  { key: "logprob",     label: "Log-Probability", icon: ChartLine },
  { key: "boxplot",     label: "Box Plots",      icon: ChartScatter },
  { key: "contact",     label: "Contact",         icon: Path },
  { key: "matrisk",     label: "Metal-at-Risk",   icon: Sparkle },
];


export default function AdvancedAnalyticsWorkspace({ projectId }) {
  const [tab, setTab] = useState("histogram");
  const dataQ = useQuery({
    queryKey: ["analytics-data", projectId],
    queryFn:  () => getAnalyticsDataSelection(projectId),
    enabled:  !!projectId,
  });

  const [filters, setFilters] = useState({
    holes: null, lithologies: null,
    depth_min: null, depth_max: null,
    grade_field: "grade", dataset: "assays",
  });

  const data = dataQ.data || {};

  const CmpBody = {
    histogram: <HistogramPanel projectId={projectId} filters={filters} />,
    logprob:   <LogProbPanel   projectId={projectId} filters={filters} />,
    boxplot:   <BoxPlotPanel   projectId={projectId} filters={filters} />,
    contact:   <ContactPanel   projectId={projectId} filters={filters} data={data} />,
    matrisk:   <MetalAtRiskPanel projectId={projectId} data={data} />,
  }[tab];

  return (
    <WizardCard title="Advanced Drillhole Analytics"
                 subtitle="Real assay + composite statistics · zero fake charts"
                 testId="w-analytics-workspace">
      {/* Data-selection strip */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-3">
        <KV k="Holes"       v={data.n_holes ?? 0}      testId="w-analytics-kpi-holes" />
        <KV k="Assays"      v={data.n_assays ?? 0}     testId="w-analytics-kpi-assays" />
        <KV k="Composites"  v={data.n_composites ?? 0} testId="w-analytics-kpi-composites" />
        <KV k="Lithologies" v={(data.lithologies || []).length} testId="w-analytics-kpi-litho" />
        <KV k="Depth range" v={data.depth_range ? `${data.depth_range[0]}–${data.depth_range[1]} m` : "—"} testId="w-analytics-kpi-depth" />
      </div>

      {/* Filters row */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-3 border-l-2 border-slate-200 pl-2">
        <FilterField label="Dataset">
          <Select value={filters.dataset}
                    onValueChange={(v) => setFilters({ ...filters, dataset: v })}>
            <SelectTrigger className="h-7 text-[11px]" data-testid="w-analytics-dataset">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="assays">Assays</SelectItem>
              <SelectItem value="composites">Composites</SelectItem>
            </SelectContent>
          </Select>
        </FilterField>
        <FilterField label="Grade field">
          <Select value={filters.grade_field}
                    onValueChange={(v) => setFilters({ ...filters, grade_field: v })}>
            <SelectTrigger className="h-7 text-[11px]" data-testid="w-analytics-grade-field">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {(data.grade_fields || ["grade"]).map((f) => (
                <SelectItem key={f} value={f}>{f}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FilterField>
        <FilterField label="Depth min (m)">
          <Input type="number" value={filters.depth_min ?? ""}
                    onChange={(e) => setFilters({ ...filters,
                                                     depth_min: e.target.value === "" ? null : Number(e.target.value) })}
                    className="h-7 text-[11px] font-mono"
                    data-testid="w-analytics-depth-min" />
        </FilterField>
        <FilterField label="Depth max (m)">
          <Input type="number" value={filters.depth_max ?? ""}
                    onChange={(e) => setFilters({ ...filters,
                                                     depth_max: e.target.value === "" ? null : Number(e.target.value) })}
                    className="h-7 text-[11px] font-mono"
                    data-testid="w-analytics-depth-max" />
        </FilterField>
        <FilterField label="Litho (comma)">
          <Input value={(filters.lithologies || []).join(", ")}
                    onChange={(e) => setFilters({ ...filters,
                                                     lithologies: e.target.value
                                                       ? e.target.value.split(",").map((x) => x.trim()).filter(Boolean)
                                                       : null })}
                    placeholder="e.g. GRN, SCHT"
                    className="h-7 text-[11px] font-mono"
                    data-testid="w-analytics-lithos" />
        </FilterField>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-1 border-b border-slate-200 mb-3">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = t.key === tab;
          return (
            <button key={t.key} type="button"
                    onClick={() => setTab(t.key)}
                    data-testid={`w-analytics-tab-${t.key}`}
                    className={`px-3 py-1.5 text-[11px] uppercase tracking-widest font-mono border-b-2 transition ${
                      active
                        ? "border-brand-blue text-brand-blue"
                        : "border-transparent text-slate-500 hover:text-slate-800"
                    }`}>
              <Icon size={11} className="inline mr-1 -mt-0.5" />
              {t.label}
            </button>
          );
        })}
      </div>

      {CmpBody}
    </WizardCard>
  );
}


function FilterField({ label, children }) {
  return (
    <div className="space-y-0.5">
      <Label className="text-[9px] uppercase tracking-widest font-mono text-slate-500">
        {label}
      </Label>
      {children}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════
// Panels
// ═══════════════════════════════════════════════════════════════════
function HistogramPanel({ projectId, filters }) {
  const [bins, setBins] = useState(30);
  const [logScale, setLogScale] = useState(false);
  const q = useMutation({
    mutationFn: () => analyticsHistogram(projectId, { ...filters, bins, log_scale: logScale }),
    onError: (e) => toast.error(e?.response?.data?.detail?.message || e.message),
  });
  const dl = useMutation({
    mutationFn: () => analyticsExport(projectId, { kind: "histogram", payload: q.data }, "histogram.csv"),
    onSuccess: () => toast.success("Histogram CSV downloaded"),
  });
  const d = q.data;
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2 items-end">
        <FilterField label="Bins">
          <Input type="number" value={bins} onChange={(e) => setBins(Number(e.target.value))}
                    className="h-7 text-[11px] font-mono w-20" data-testid="w-histogram-bins" />
        </FilterField>
        <label className="flex items-center gap-1 text-[11px] font-mono">
          <input type="checkbox" checked={logScale} onChange={(e) => setLogScale(e.target.checked)}
                    data-testid="w-histogram-log" />
          log scale
        </label>
        <Button size="sm" onClick={() => q.mutate()} disabled={q.isPending}
                    className="h-7 text-[11px] bg-brand-blue text-white"
                    data-testid="w-histogram-run">
          <Sparkle size={11} className="mr-1" />
          {q.isPending ? "Computing…" : "Compute"}
        </Button>
        {d && (
          <Button size="sm" variant="outline" onClick={() => dl.mutate()}
                      className="h-7 text-[11px]"
                      data-testid="w-histogram-dl">
            <DownloadSimple size={11} className="mr-1" /> CSV
          </Button>
        )}
      </div>
      {!d && q.isIdle && (
        <StatusRow tone="info">Click <b>Compute</b> to build a histogram from real assay data.</StatusRow>
      )}
      {d && <HistogramChart data={d} logScale={logScale} />}
      {d && <StatsGrid stats={d.statistics} />}
      {d?.n_zero_or_neg > 0 && logScale && (
        <div className="text-[10px] text-amber-600 flex items-center gap-1">
          <Warning size={11} /> {d.n_zero_or_neg} zero-or-negative sample(s) excluded from log scale.
        </div>
      )}
    </div>
  );
}


function HistogramChart({ data, logScale }) {
  const bins = data?.bins || [];
  if (!bins.length) return null;
  const maxCount = Math.max(...bins.map((b) => b.count));
  const W = 780, H = 240, PAD_L = 40, PAD_B = 30, PAD_T = 8, PAD_R = 12;
  const chartW = W - PAD_L - PAD_R;
  const chartH = H - PAD_T - PAD_B;
  const barW = chartW / bins.length;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-56 bg-white border border-slate-200 rounded"
          data-testid="w-histogram-chart">
      {/* Y axis */}
      <line x1={PAD_L} y1={PAD_T} x2={PAD_L} y2={PAD_T + chartH} stroke="#94A3B8" />
      <line x1={PAD_L} y1={PAD_T + chartH} x2={PAD_L + chartW} y2={PAD_T + chartH} stroke="#94A3B8" />
      {[0, 0.5, 1].map((t) => (
        <text key={t} x={PAD_L - 5} y={PAD_T + chartH - t * chartH + 3}
                textAnchor="end" fontSize="8" fill="#64748B">
          {Math.round(maxCount * t)}
        </text>
      ))}
      {bins.map((b, i) => {
        const h = (b.count / maxCount) * chartH;
        return (
          <rect key={i}
                  x={PAD_L + i * barW + 1}
                  y={PAD_T + chartH - h}
                  width={barW - 2}
                  height={h}
                  fill="#3B82F6" opacity="0.75">
            <title>{`${b.lo.toFixed(3)} – ${b.hi.toFixed(3)}${logScale ? " (log10)" : ""} · n=${b.count}`}</title>
          </rect>
        );
      })}
      {/* X axis labels — first, middle, last */}
      {[0, Math.floor(bins.length / 2), bins.length - 1].map((i) => (
        <text key={i} x={PAD_L + i * barW + barW / 2} y={H - 10}
                textAnchor="middle" fontSize="8" fill="#475569">
          {bins[i].lo.toFixed(2)}
        </text>
      ))}
    </svg>
  );
}


function LogProbPanel({ projectId, filters }) {
  const q = useMutation({
    mutationFn: () => analyticsLogProbability(projectId, { ...filters, log_scale: true }),
    onError:    (e) => toast.error(e?.response?.data?.detail?.message || e.message),
  });
  const dl = useMutation({
    mutationFn: () => analyticsExport(projectId, { kind: "log_probability", payload: q.data }, "log_probability.csv"),
    onSuccess: () => toast.success("Log-probability CSV downloaded"),
  });
  const d = q.data;
  return (
    <div className="space-y-3">
      <div className="flex gap-2 items-end">
        <Button size="sm" onClick={() => q.mutate()} disabled={q.isPending}
                    className="h-7 text-[11px] bg-brand-blue text-white"
                    data-testid="w-logprob-run">
          <Sparkle size={11} className="mr-1" />
          {q.isPending ? "Computing…" : "Compute"}
        </Button>
        {d && (
          <Button size="sm" variant="outline" onClick={() => dl.mutate()}
                      className="h-7 text-[11px]"
                      data-testid="w-logprob-dl">
            <DownloadSimple size={11} className="mr-1" /> CSV
          </Button>
        )}
      </div>
      {!d && q.isIdle && (
        <StatusRow tone="info">
          Click <b>Compute</b> to build a log-probability plot. Zero and negative
          values are excluded automatically and reported.
        </StatusRow>
      )}
      {d && <LogProbChart data={d} />}
      {d && (
        <div className="text-[11px] font-mono text-slate-500 border-l-2 border-slate-300 pl-2">
          {d.notes}
        </div>
      )}
      {d && <StatsGrid stats={d.statistics} />}
    </div>
  );
}


function LogProbChart({ data }) {
  const pts = data?.points || [];
  if (!pts.length) return null;
  const xs = pts.map((p) => p.value);
  const zs = pts.map((p) => p.z);
  const xmin = Math.min(...xs), xmax = Math.max(...xs);
  const zmin = -3.5, zmax = 3.5;
  const W = 780, H = 240, PAD_L = 40, PAD_B = 30, PAD_T = 8, PAD_R = 12;
  const chartW = W - PAD_L - PAD_R;
  const chartH = H - PAD_T - PAD_B;
  const px = (v) => PAD_L + ((v - xmin) / Math.max(1e-9, xmax - xmin)) * chartW;
  const py = (v) => PAD_T + (1 - (v - zmin) / (zmax - zmin)) * chartH;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-56 bg-white border border-slate-200 rounded"
          data-testid="w-logprob-chart">
      <line x1={PAD_L} y1={PAD_T} x2={PAD_L} y2={PAD_T + chartH} stroke="#94A3B8" />
      <line x1={PAD_L} y1={PAD_T + chartH} x2={PAD_L + chartW} y2={PAD_T + chartH} stroke="#94A3B8" />
      {[-3, -2, -1, 0, 1, 2, 3].map((z) => (
        <g key={z}>
          <line x1={PAD_L} y1={py(z)} x2={PAD_L + chartW} y2={py(z)}
                  stroke="#E2E8F0" strokeDasharray="2 2" />
          <text x={PAD_L - 5} y={py(z) + 3} textAnchor="end" fontSize="8" fill="#64748B">{z}</text>
        </g>
      ))}
      {pts.map((p, i) => (
        <circle key={i} cx={px(p.value)} cy={py(p.z)} r="1.6" fill="#0EA5E9">
          <title>{`log10 grade ${p.value.toFixed(3)} · z ${p.z} · P${p.percentile}%`}</title>
        </circle>
      ))}
    </svg>
  );
}


function BoxPlotPanel({ projectId, filters }) {
  const [groupBy, setGroupBy] = useState("lithology");
  const q = useMutation({
    mutationFn: () => analyticsBoxPlot(projectId, { ...filters, group_by: groupBy }),
    onError: (e) => toast.error(e?.response?.data?.detail?.message || e.message),
  });
  const dl = useMutation({
    mutationFn: () => analyticsExport(projectId, { kind: "box_plot", payload: q.data }, "box_plot.csv"),
    onSuccess: () => toast.success("Box-plot CSV downloaded"),
  });
  const d = q.data;
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2 items-end">
        <FilterField label="Group by">
          <Select value={groupBy} onValueChange={setGroupBy}>
            <SelectTrigger className="h-7 text-[11px] w-40" data-testid="w-boxplot-groupby">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="lithology">Lithology</SelectItem>
              <SelectItem value="hole">Hole</SelectItem>
              <SelectItem value="depth_band">Depth band</SelectItem>
            </SelectContent>
          </Select>
        </FilterField>
        <Button size="sm" onClick={() => q.mutate()} disabled={q.isPending}
                    className="h-7 text-[11px] bg-brand-blue text-white"
                    data-testid="w-boxplot-run">
          <Sparkle size={11} className="mr-1" />
          {q.isPending ? "Computing…" : "Compute"}
        </Button>
        {d && (
          <Button size="sm" variant="outline" onClick={() => dl.mutate()}
                      className="h-7 text-[11px]"
                      data-testid="w-boxplot-dl">
            <DownloadSimple size={11} className="mr-1" /> CSV
          </Button>
        )}
      </div>
      {!d && q.isIdle && (
        <StatusRow tone="info">Click <b>Compute</b> to see box plots by {groupBy}.</StatusRow>
      )}
      {d && <BoxPlotChart data={d} />}
    </div>
  );
}


function BoxPlotChart({ data }) {
  const boxes = data?.boxes || [];
  if (!boxes.length) return null;
  const maxV = Math.max(...boxes.map((b) => b.max)) * 1.1;
  const minV = Math.min(...boxes.map((b) => b.min));
  const W = 780, H = 280, PAD_L = 40, PAD_B = 40, PAD_T = 10, PAD_R = 12;
  const chartW = W - PAD_L - PAD_R;
  const chartH = H - PAD_T - PAD_B;
  const bandW = chartW / boxes.length;
  const py = (v) => PAD_T + (1 - (v - minV) / Math.max(1e-9, maxV - minV)) * chartH;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-64 bg-white border border-slate-200 rounded"
          data-testid="w-boxplot-chart">
      <line x1={PAD_L} y1={PAD_T} x2={PAD_L} y2={PAD_T + chartH} stroke="#94A3B8" />
      <line x1={PAD_L} y1={PAD_T + chartH} x2={PAD_L + chartW} y2={PAD_T + chartH} stroke="#94A3B8" />
      {[0, 0.25, 0.5, 0.75, 1].map((t) => (
        <g key={t}>
          <line x1={PAD_L} y1={PAD_T + chartH - t * chartH}
                  x2={PAD_L + chartW} y2={PAD_T + chartH - t * chartH}
                  stroke="#E2E8F0" strokeDasharray="2 2" />
          <text x={PAD_L - 5} y={PAD_T + chartH - t * chartH + 3}
                  textAnchor="end" fontSize="8" fill="#64748B">
            {(minV + t * (maxV - minV)).toFixed(2)}
          </text>
        </g>
      ))}
      {boxes.map((b, i) => {
        const cx = PAD_L + i * bandW + bandW / 2;
        const boxW = bandW * 0.5;
        return (
          <g key={b.group}>
            <line x1={cx} y1={py(b.min)} x2={cx} y2={py(b.q1)} stroke="#64748B" />
            <line x1={cx} y1={py(b.q3)} x2={cx} y2={py(b.max)} stroke="#64748B" />
            <rect x={cx - boxW / 2} y={py(b.q3)}
                    width={boxW} height={py(b.q1) - py(b.q3)}
                    fill="#93C5FD" stroke="#1D4ED8" />
            <line x1={cx - boxW / 2} y1={py(b.median)}
                    x2={cx + boxW / 2} y2={py(b.median)}
                    stroke="#1D4ED8" strokeWidth="2" />
            {b.outliers.map((o, j) => (
              <circle key={j} cx={cx} cy={py(o)} r="1.5" fill="#EF4444" />
            ))}
            <text x={cx} y={H - 20} textAnchor="middle" fontSize="8" fill="#334155">
              {String(b.group).slice(0, 10)}
            </text>
            <text x={cx} y={H - 8} textAnchor="middle" fontSize="7" fill="#64748B">
              n={b.n}
            </text>
          </g>
        );
      })}
    </svg>
  );
}


function ContactPanel({ projectId, filters, data }) {
  const lithos = data.lithologies || [];
  const [inside, setInside] = useState("");
  const [outside, setOutside] = useState("");
  const [distance, setDistance] = useState(30);
  const [nBands, setNBands] = useState(12);
  const q = useMutation({
    mutationFn: () => analyticsContactAnalysis(projectId, {
      ...filters,
      contact_kind: "lithology",
      inside_code: inside, outside_code: outside,
      max_distance_m: distance, n_bands: nBands,
    }),
    onError: (e) => toast.error(e?.response?.data?.detail?.message || e.message),
  });
  const dl = useMutation({
    mutationFn: () => analyticsExport(projectId, { kind: "contact_analysis", payload: q.data }, "contact_analysis.csv"),
    onSuccess: () => toast.success("Contact CSV downloaded"),
  });
  const d = q.data;
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2 items-end">
        <FilterField label="Inside code">
          <Select value={inside} onValueChange={setInside}>
            <SelectTrigger className="h-7 text-[11px] w-32" data-testid="w-contact-inside">
              <SelectValue placeholder="…" />
            </SelectTrigger>
            <SelectContent>
              {lithos.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
        </FilterField>
        <FilterField label="Outside code">
          <Select value={outside} onValueChange={setOutside}>
            <SelectTrigger className="h-7 text-[11px] w-32" data-testid="w-contact-outside">
              <SelectValue placeholder="…" />
            </SelectTrigger>
            <SelectContent>
              {lithos.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
        </FilterField>
        <FilterField label="Max dist (m)">
          <Input type="number" value={distance} onChange={(e) => setDistance(Number(e.target.value))}
                    className="h-7 text-[11px] w-20 font-mono" data-testid="w-contact-distance" />
        </FilterField>
        <FilterField label="Bands">
          <Input type="number" value={nBands} onChange={(e) => setNBands(Number(e.target.value))}
                    className="h-7 text-[11px] w-16 font-mono" data-testid="w-contact-bands" />
        </FilterField>
        <Button size="sm" onClick={() => q.mutate()}
                    disabled={!inside || !outside || q.isPending}
                    className="h-7 text-[11px] bg-brand-blue text-white"
                    data-testid="w-contact-run">
          <Sparkle size={11} className="mr-1" />
          {q.isPending ? "Computing…" : "Compute"}
        </Button>
        {d && (
          <Button size="sm" variant="outline" onClick={() => dl.mutate()}
                      className="h-7 text-[11px]"
                      data-testid="w-contact-dl">
            <DownloadSimple size={11} className="mr-1" /> CSV
          </Button>
        )}
      </div>
      {!d && q.isIdle && (
        <StatusRow tone="info">
          Pick <b>inside</b> and <b>outside</b> lithology codes to analyse the
          grade profile across their contact.
        </StatusRow>
      )}
      {d && (
        <>
          <div className="grid grid-cols-3 gap-2">
            <KV k="Recommended" v={d.recommended_boundary}          testId="w-contact-recommendation" />
            <KV k="Confidence"  v={d.confidence}                     testId="w-contact-confidence" />
            <KV k="Band width"  v={`${d.band_width_m} m`}            testId="w-contact-bandw" />
          </div>
          <div className="rounded border border-slate-200 overflow-x-auto max-h-72">
            <table className="w-full text-[11px] font-mono">
              <thead className="bg-slate-100 text-slate-700 sticky top-0">
                <tr>
                  <th className="text-left px-2 py-1">Band</th>
                  <th className="text-right px-2 py-1">Distance</th>
                  <th className="text-right px-2 py-1">Inside n</th>
                  <th className="text-right px-2 py-1">Inside mean</th>
                  <th className="text-right px-2 py-1">Outside n</th>
                  <th className="text-right px-2 py-1">Outside mean</th>
                  <th className="text-right px-2 py-1">Ratio</th>
                </tr>
              </thead>
              <tbody>
                {d.bands.map((b) => (
                  <tr key={b.band} className="border-t border-slate-200"
                        data-testid={`w-contact-band-${b.band}`}>
                    <td className="px-2 py-1 font-bold">{b.band}</td>
                    <td className="text-right px-2 py-1">{b.distance_from_m}–{b.distance_to_m} m</td>
                    <td className="text-right px-2 py-1">{b.inside.n}</td>
                    <td className="text-right px-2 py-1">{b.inside.mean ?? "—"}</td>
                    <td className="text-right px-2 py-1">{b.outside.n}</td>
                    <td className="text-right px-2 py-1">{b.outside.mean ?? "—"}</td>
                    <td className="text-right px-2 py-1">{b.grade_ratio ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="text-[10px] italic text-slate-500 border-l-2 border-amber-300 pl-2">
            {d.note}
          </div>
        </>
      )}
    </div>
  );
}


function MetalAtRiskPanel({ projectId, data }) {
  const [unit, setUnit] = useState("g/t");
  const [scenarios, setScenarios] = useState([
    { scenario: "Top-cut applied", reason: "Cap high-grade outliers", adjusted_tonnes: null, adjusted_grade: null },
    { scenario: "Density reduced 5%", reason: "Sensitivity — density -5%", adjusted_tonnes: null, adjusted_grade: null },
  ]);
  const q = useMutation({
    mutationFn: () => analyticsMetalAtRisk(projectId, { unit, scenarios }),
    onError: (e) => toast.error(e?.response?.data?.detail?.message || e.message),
  });
  const dl = useMutation({
    mutationFn: () => analyticsExport(projectId, { kind: "metal_at_risk", payload: q.data }, "metal_at_risk.csv"),
    onSuccess: () => toast.success("Metal-at-Risk CSV downloaded"),
  });
  const d = q.data;

  const updateScenario = (i, field, v) => {
    const next = [...scenarios];
    next[i] = { ...next[i], [field]: v };
    setScenarios(next);
  };
  const addScenario = () => setScenarios([...scenarios,
    { scenario: "New scenario", reason: "", adjusted_tonnes: null, adjusted_grade: null }]);

  return (
    <div className="space-y-3">
      <div className="flex gap-2 items-end">
        <FilterField label="Unit">
          <Select value={unit} onValueChange={setUnit}>
            <SelectTrigger className="h-7 text-[11px] w-28" data-testid="w-matrisk-unit">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {(data.commodity_units || ["g/t", "%", "ppm", "kg/t", "carats/t", "TGC%", "oxide%"]).map((u) => (
                <SelectItem key={u} value={u}>{u}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FilterField>
        <Button size="sm" onClick={() => q.mutate()} disabled={q.isPending}
                    className="h-7 text-[11px] bg-brand-blue text-white"
                    data-testid="w-matrisk-run">
          <Sparkle size={11} className="mr-1" />
          {q.isPending ? "Computing…" : "Compute"}
        </Button>
        {d && (
          <Button size="sm" variant="outline" onClick={() => dl.mutate()}
                      className="h-7 text-[11px]"
                      data-testid="w-matrisk-dl">
            <DownloadSimple size={11} className="mr-1" /> CSV
          </Button>
        )}
        <Button size="sm" variant="outline" onClick={addScenario}
                    className="h-7 text-[11px] ml-auto"
                    data-testid="w-matrisk-add">
          + Scenario
        </Button>
      </div>

      <div className="rounded border border-slate-200 overflow-x-auto">
        <table className="w-full text-[11px] font-mono">
          <thead className="bg-slate-100 text-slate-700">
            <tr>
              <th className="text-left px-2 py-1">Scenario</th>
              <th className="text-left px-2 py-1">Reason</th>
              <th className="text-right px-2 py-1">Adj. tonnes</th>
              <th className="text-right px-2 py-1">Adj. grade</th>
            </tr>
          </thead>
          <tbody>
            {scenarios.map((s, i) => (
              <tr key={i} className="border-t border-slate-200"
                    data-testid={`w-matrisk-row-${i}`}>
                <td className="px-2 py-1">
                  <Input value={s.scenario} onChange={(e) => updateScenario(i, "scenario", e.target.value)}
                            className="h-6 text-[10px] font-mono" />
                </td>
                <td className="px-2 py-1">
                  <Input value={s.reason} onChange={(e) => updateScenario(i, "reason", e.target.value)}
                            className="h-6 text-[10px] font-mono" />
                </td>
                <td className="px-2 py-1">
                  <Input type="number" value={s.adjusted_tonnes ?? ""}
                            onChange={(e) => updateScenario(i, "adjusted_tonnes",
                              e.target.value === "" ? null : Number(e.target.value))}
                            className="h-6 text-[10px] font-mono text-right"
                            placeholder="use base" />
                </td>
                <td className="px-2 py-1">
                  <Input type="number" step="0.01" value={s.adjusted_grade ?? ""}
                            onChange={(e) => updateScenario(i, "adjusted_grade",
                              e.target.value === "" ? null : Number(e.target.value))}
                            className="h-6 text-[10px] font-mono text-right"
                            placeholder="use base" />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {d && (
        <div className="space-y-3">
          <div className="grid grid-cols-4 gap-2">
            <KV k="Base tonnes" v={fmt(d.base.tonnes / 1e6, 2) + " Mt"} testId="w-matrisk-base-tonnes" />
            <KV k="Base grade" v={d.base.grade.toFixed(4)}              testId="w-matrisk-base-grade" />
            <KV k="Base contained" v={`${fmt(d.base.contained, 0)} ${d.base.contained_unit}`} testId="w-matrisk-base-contained" />
            <KV k="Formula" v={d.conversion?.note?.slice(0, 40)}         testId="w-matrisk-formula" />
          </div>
          <div className="rounded border border-slate-200 overflow-x-auto">
            <table className="w-full text-[11px] font-mono">
              <thead className="bg-slate-100 text-slate-700">
                <tr>
                  <th className="text-left px-2 py-1">Scenario</th>
                  <th className="text-right px-2 py-1">Tonnes</th>
                  <th className="text-right px-2 py-1">Grade</th>
                  <th className="text-right px-2 py-1">Contained</th>
                  <th className="text-right px-2 py-1">Δ abs</th>
                  <th className="text-right px-2 py-1">Δ %</th>
                </tr>
              </thead>
              <tbody>
                {d.scenarios.map((s, i) => (
                  <tr key={i} className="border-t border-slate-200"
                        data-testid={`w-matrisk-result-${i}`}>
                    <td className="px-2 py-1 font-bold">{s.scenario}</td>
                    <td className="text-right px-2 py-1">{fmt(s.adjusted_tonnes / 1e6, 2)} Mt</td>
                    <td className="text-right px-2 py-1">{s.adjusted_grade.toFixed(4)}</td>
                    <td className="text-right px-2 py-1">{fmt(s.contained, 0)}</td>
                    <td className={`text-right px-2 py-1 ${s.abs_diff >= 0 ? "text-emerald-600" : "text-red-600"}`}>{fmt(s.abs_diff, 1)}</td>
                    <td className={`text-right px-2 py-1 ${(s.pct_diff ?? 0) >= 0 ? "text-emerald-600" : "text-red-600"}`}>{s.pct_diff}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}


function StatsGrid({ stats }) {
  if (!stats) return null;
  const items = [
    ["Count", stats.count], ["Min", stats.min], ["Max", stats.max],
    ["Mean", stats.mean],   ["Median", stats.median],
    ["Std dev", stats.stdev], ["CV", stats.cv],
    ["Skewness", stats.skewness], ["Kurtosis", stats.kurtosis],
    ["Below detection", stats.below_detection], ["Missing", stats.missing],
  ];
  return (
    <div className="grid grid-cols-3 md:grid-cols-6 gap-2 text-[10px] font-mono">
      {items.map(([k, v]) => (
        <div key={k} className="rounded border border-slate-200 p-1 bg-white"
              data-testid={`w-stats-${k.replaceAll(" ", "-").toLowerCase()}`}>
          <div className="uppercase text-[8px] text-slate-500 tracking-widest">{k}</div>
          <div className="font-bold text-slate-800">{v ?? "—"}</div>
        </div>
      ))}
    </div>
  );
}


function fmt(n, digits = 0) {
  if (n === null || n === undefined || Number.isNaN(n)) return "—";
  return Number(n).toLocaleString(undefined, {
    maximumFractionDigits: digits, minimumFractionDigits: 0,
  });
}
